import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import styles from './ManagementCompany.module.css';
import {
  Search,
  ChevronLeft,
  ChevronRight,
  Edit,
  Trash,
  Plus,
  Building,
  Shield,
  Phone,
  Mail,
  Home,
  User,
  Filter,
  ChevronDown,
  MoreVertical,
  FileSpreadsheet,
  Menu,
  Download
} from 'lucide-react';
import { getListManagementCompanies, deleteManagementCompany } from '../../Fetch/fetchManagementCompany';
import ManagementCompanyForm from './ManagementCompanyForm';
import ErrorPopup from '../../components/ErrorPopup/ErrorPopup';
import { useApiWithErrorHandling } from '../../hooks/useApiWithErrorHandling';

// Management Company Action Menu Modal Component
const ActionMenu = ({ company, onEdit, onDelete, loading }) => {
  const [isOpen, setIsOpen] = useState(false);

  const actions = [
    {
      label: '✏️ ערוך',
      onClick: () => onEdit(company),
      disabled: loading
    },
    {
      label: '🗑️ מחק',
      onClick: () => onDelete(company.ManagementCompanyID),
      disabled: loading,
      isDanger: true
    }
  ];

  const handleActionClick = (action) => {
    if (!action.disabled) {
      action.onClick();
      setIsOpen(false);
    }
  };

  return (
    <>
      <button
        className={`${styles.actionMenuTrigger} ${loading ? styles.disabled : ''}`}
        onClick={() => setIsOpen(true)}
        disabled={loading}
        aria-label="פתח תפריט פעולות"
      >
        <span>⋯</span>
      </button>

      {isOpen && createPortal(
        <div className={styles.modalOverlay} onClick={() => setIsOpen(false)}>
          <div className={styles.actionMenuModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.actionMenuHeader}>
              <h3>פעולות</h3>
              <button
                className={styles.closeButton}
                onClick={() => setIsOpen(false)}
                aria-label="סגור"
              >
                ✕
              </button>
            </div>
            <div className={styles.actionMenuContent}>
              {actions.map((action, index) => (
                <button
                  key={index}
                  className={`${styles.actionMenuItem} ${action.isDanger ? styles.dangerAction : ''} ${action.disabled ? styles.disabled : ''}`}
                  onClick={() => handleActionClick(action)}
                  disabled={action.disabled}
                >
                  {action.label}
                </button>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

// ManagementCompanyCard Component for Mobile View
const ManagementCompanyCard = ({ company, onEdit, onDelete, onToggleStatus }) => (
  <div className={styles.dataCard}>
    <div className={styles.cardHeader}>
      <div className={styles.cardTitle}>
        <Building size={16} className={styles.cardIcon} />
        <span>{company.Name}</span>
      </div>
      <button
        className={`${styles.statusBadge} ${company.IsActive ? styles.statusActive : styles.statusInactive}`}
        onClick={() => onToggleStatus(company.ManagementCompanyID)}
        title={company.IsActive ? 'לחץ להשבתה' : 'לחץ להפעלה'}
      >
        {company.IsActive ? 'פעיל' : 'לא פעיל'}
      </button>
    </div>
    <div className={styles.cardBody}>
      <div className={styles.cardInfo}>
        <User size={14} />
        <span>איש קשר: {company.ContactPerson || 'לא צוין'}</span>
      </div>
      <div className={styles.cardInfo}>
        <Mail size={14} />
        <span>{company.Email || 'לא צוין'}</span>
      </div>
      <div className={styles.cardInfo}>
        <Phone size={14} />
        <span>{company.Phone || 'לא צוין'}</span>
      </div>
      <div className={styles.cardInfo}>
        <Home size={14} />
        <span>כתובת: {company.Address || 'לא צוין'}</span>
      </div>
      <div className={styles.cardActions}>
        <button
          className={styles.editButton}
          onClick={() => onEdit(company)}
          title="עריכה"
        >
          <Edit size={16} />
        </button>
        <button
          className={styles.deleteButton}
          onClick={() => onDelete(company.ManagementCompanyID)}
          title="מחיקה"
        >
          <Trash size={16} />
        </button>
      </div>
    </div>
  </div>
);

const ManagementCompany = () => {
  // State declarations - initialize all states first
  const [companies, setCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('active'); // Changed default to 'active'
  const [currentPage, setCurrentPage] = useState(1);
  const [companiesPerPage, setCompaniesPerPage] = useState(10);
  const [showForm, setShowForm] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isExportDropdownOpen, setIsExportDropdownOpen] = useState(false);
  const [isMobileDropdownOpen, setIsMobileDropdownOpen] = useState(false);
  const mobileDropdownRef = useRef(null);
  const [errorPopup, setErrorPopup] = useState({
    isOpen: false,
    title: '',
    message: ''
  });

  const { executeWithErrorHandling } = useApiWithErrorHandling();

  // Helper function to normalize company data from API
  const normalizeCompanyData = (apiData) => {
    if (!Array.isArray(apiData)) {
      return [];
    }

    return apiData.map(company => ({
      ManagementCompanyID: company.ManagementCompanyID || company.managementCompanyID || company.id,
      Name: company.Name || company.name || '',
      ContactPerson: company.ContactPerson || company.contactPerson || '',
      Phone: company.Phone || company.phone || '',
      Email: company.Email || company.email || '',
      Address: company.Address || company.address || '',
      BuildingID: company.BuildingID || company.buildingID || 0,
      IsActive: company.IsActive !== undefined ? company.IsActive : company.isActive !== undefined ? company.isActive : true
    }));
  };

  // Statistics - calculate after companies is available
  const totalCompanies = companies.length;
  const activeCompanies = companies.filter(c => c.IsActive).length;
  const inactiveCompanies = companies.filter(c => !c.IsActive).length;

  // Load companies data
  const loadCompanies = async () => {
    try {
      setLoading(true);
      const userData = JSON.parse(localStorage.getItem('user'));
      const buildingId = userData?.buildingId;
      
      if (!buildingId) {
        setCompanies([]);
        return;
      }

      const data = await executeWithErrorHandling(
        () => getListManagementCompanies(buildingId)
      );

      // Normalize the data to ensure consistent field names
      const normalizedData = normalizeCompanyData(data);

      setCompanies(normalizedData);
      
      // Reset filters and pagination when loading new building data
      setSearchTerm('');
      setCurrentPage(1);
      
    } catch (error) {
      setCompanies([]);
      if (error.message !== 'NETWORK_ERROR') {
        alert('שגיאה בטעינת חברות הניהול: ' + error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // Effects
  useEffect(() => {
    loadCompanies();
  }, []);

  // Add event listener for building changes
  useEffect(() => {
    const handleBuildingChange = () => {
      loadCompanies();
    };

    window.addEventListener('buildingChanged', handleBuildingChange);
    window.addEventListener('companyDataChanged', handleBuildingChange);

    return () => {
      window.removeEventListener('buildingChanged', handleBuildingChange);
      window.removeEventListener('companyDataChanged', handleBuildingChange);
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(`.${styles.actionButtonGroup}`)) {
        setShowDropdown(false);
      }
      if (!event.target.closest(`.${styles.exportButton}`) && !event.target.closest(`.${styles.exportDropdownMenu}`)) {
        setIsExportDropdownOpen(false);
      }
      if (mobileDropdownRef.current && !mobileDropdownRef.current.contains(event.target)) {
        setIsMobileDropdownOpen(false);
      }
    };

    if (showDropdown || isExportDropdownOpen || isMobileDropdownOpen) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showDropdown, isExportDropdownOpen, isMobileDropdownOpen]);

  // Filter companies based on search term and status - this effect sets filteredCompanies
  useEffect(() => {
    let filtered = companies;

    // Filter by status
    if (statusFilter === 'active') {
      filtered = filtered.filter(company => company.IsActive);
    } else if (statusFilter === 'inactive') {
      filtered = filtered.filter(company => !company.IsActive);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(company => {
        const searchLower = searchTerm.toLowerCase();
        
        // Check for active status search terms
        const isActiveSearch = 
          searchLower === 'true' || 
          searchLower === 'פעיל' || 
          searchLower === 'active' ||
          searchLower === 'פעילה';
          
        const isInactiveSearch = 
          searchLower === 'false' || 
          searchLower === 'לא פעיל' || 
          searchLower === 'inactive' ||
          searchLower === 'לא פעילה' ||
          searchLower === 'לא-פעיל' ||
          searchLower === 'לא-פעילה';
        
        // If searching for status, filter by IsActive
        if (isActiveSearch) {
          return company.IsActive === true;
        }
        
        if (isInactiveSearch) {
          return company.IsActive === false;
        }
        
        // Otherwise, search in regular fields
        return (
          company.Name?.toLowerCase().includes(searchLower) ||
          company.ContactPerson?.toLowerCase().includes(searchLower) ||
          company.Email?.toLowerCase().includes(searchLower) ||
          company.Phone?.includes(searchTerm) ||
          company.Address?.toLowerCase().includes(searchLower)
        );
      });
    }

    setFilteredCompanies(filtered);
    setCurrentPage(1);
  }, [searchTerm, statusFilter, companies]);

  // Debug effect - keep minimal logging
  useEffect(() => {
  }, [companies, filteredCompanies]);

  // Calculate pagination AFTER filteredCompanies is set
  const indexOfLastCompany = currentPage * companiesPerPage;
  const indexOfFirstCompany = indexOfLastCompany - companiesPerPage;
  const currentCompanies = filteredCompanies.slice(indexOfFirstCompany, indexOfLastCompany);
  const totalPages = Math.ceil(filteredCompanies.length / companiesPerPage);

  // Clear filters function
  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('active'); // Reset to 'active' instead of 'all'
  };

  // Excel Export Function
  const exportToExcel = async (exportAllPages = false) => {
    try {
      setIsExporting(true);

      // Dynamic import of SheetJS
      const XLSX = await import('xlsx');

      // Determine which data to export
      const dataToExport = exportAllPages ? filteredCompanies : currentCompanies;

      if (dataToExport.length === 0) {
        alert('אין נתונים לייצוא');
        return;
      }

      // Prepare data for Excel
      const excelData = dataToExport.map((company, index) => ({
        'מספר רשומה': index + 1,
        'מזהה': company.ManagementCompanyID,
        'שם החברה': company.Name || '',
        'איש קשר': company.ContactPerson || 'לא צוין',
        'טלפון': company.Phone || 'לא צוין',
        'דוא"ל': company.Email || 'לא צוין',
        'כתובת': company.Address || 'לא צוין',
        'סטטוס': company.IsActive ? 'פעיל' : 'לא פעיל',
        'מזהה בניין': company.BuildingID || ''
      }));

      // Create workbook
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);

      // Set column widths
      const colWidths = [
        { wch: 8 },  // מספר רשומה
        { wch: 10 }, // מזהה
        { wch: 25 }, // שם החברה
        { wch: 20 }, // איש קשר
        { wch: 15 }, // טלפון
        { wch: 25 }, // דוא"ל
        { wch: 30 }, // כתובת
        { wch: 10 }, // סטטוס
        { wch: 12 }  // מזהה בניין
      ];
      ws['!cols'] = colWidths;

      // Add worksheet to workbook
      const currentDate = new Date().toLocaleDateString('he-IL');
      const sheetName = 'חברות ניהול';

      XLSX.utils.book_append_sheet(wb, ws, sheetName);

      // Generate filename
      const filename = exportAllPages
        ? `חברות_ניהול_כל_הנתונים_${currentDate.replace(/\//g, '-')}.xlsx`
        : `חברות_ניהול_עמוד_${currentPage}_${currentDate.replace(/\//g, '-')}.xlsx`;

      // Save file
      XLSX.writeFile(wb, filename);

      alert(exportAllPages 
        ? `יוצא בהצלחה! ${dataToExport.length} חברות ניהול`
        : `יוצא בהצלחה! ${dataToExport.length} חברות ניהול (עמוד ${currentPage})`
      );

    } catch (error) {
      alert('שגיאה בייצוא הקובץ: ' + error.message);
    } finally {
      setIsExporting(false);
    }
  };

  const paginate = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const handleAddCompany = () => {
    setEditingCompany(null);
    setShowForm(true);
    setShowDropdown(false);
  };

  const handleEditCompany = (company) => {
    setEditingCompany(company);
    setShowForm(true);
  };

  const handleDeleteCompany = async (id) => {
    if (window.confirm('האם אתה בטוח שברצונך למחוק חברת ניהול זו?')) {
      try {
        await executeWithErrorHandling(
          () => deleteManagementCompany(id)
        );
        const updatedCompanies = companies.filter(company => company.ManagementCompanyID !== id);
        setCompanies(updatedCompanies);
      } catch (error) {
        if (error.message !== 'NETWORK_ERROR') {
          alert('שגיאה במחיקת חברת הניהול: ' + error.message);
        }
      }
    }
  };

  const handleToggleActiveStatus = async (companyId) => {
    try {
      
      // Call the toggle API endpoint
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://localhost:7204'}/api/ManagementCompany/ToggleActiveStatus`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ ManagementCompanyID: companyId })
      });

      if (!response.ok) {
        throw new Error('Failed to toggle status');
      }

      const result = await response.json();
      
      // Update the local state
      setCompanies(prev => prev.map(company => 
        company.ManagementCompanyID === companyId 
          ? { ...company, IsActive: result.isActive }
          : company
      ));
      
    } catch (error) {
      alert('שגיאה בשינוי סטטוס חברת הניהול: ' + error.message);
    }
  };

  const handleCompanySaved = (savedCompany) => {
    
    // Normalize the saved company data to match our expected format
    const normalizedSavedCompany = {
      ManagementCompanyID: savedCompany.ManagementCompanyID || savedCompany.managementCompanyID,
      Name: savedCompany.Name || savedCompany.name,
      ContactPerson: savedCompany.ContactPerson || savedCompany.contactPerson,
      Phone: savedCompany.Phone || savedCompany.phone,
      Email: savedCompany.Email || savedCompany.email,
      Address: savedCompany.Address || savedCompany.address,
      BuildingID: savedCompany.BuildingID || savedCompany.buildingID,
      IsActive: savedCompany.IsActive !== undefined ? savedCompany.IsActive : true
    };

    if (editingCompany) {
      // Update existing company
      setCompanies(prev => {
        const updated = prev.map(c =>
          c.ManagementCompanyID === normalizedSavedCompany.ManagementCompanyID ? normalizedSavedCompany : c
        );
        return updated;
      });
    } else {
      // Add new company
      setCompanies(prev => [...prev, normalizedSavedCompany]);
    }
    
    setShowForm(false);
    setEditingCompany(null);
    
    // Optional: Force reload from API to ensure data consistency
    // setTimeout(() => loadCompanies(), 500);
  };

  // Get building ID for form
  const getBuildingId = () => {
    const userData = JSON.parse(localStorage.getItem('user'));
    return userData?.buildingId;
  };

  // Show CompanyForm if form is active
  if (showForm) {
    return (
      <ManagementCompanyForm
        company={editingCompany}
        onSave={handleCompanySaved}
        onCancel={() => setShowForm(false)}
        buildingId={getBuildingId()}
      />
    );
  }

  return (
    <div className={styles.managementCompany}>
      <div className={styles.header}>
        <h1>ניהול חברות ניהול</h1>
        <div className={styles.stats}>
          <div className={styles.statCard}>
            <Building size={24} className={styles.statIcon} />
            <div>
              <h3>סה"כ חברות ניהול</h3>
              <p className={styles.statValue}>{totalCompanies}</p>
            </div>
          </div>
          <div className={styles.statCard}>
            <Shield size={24} className={styles.statIcon} style={{color: '#28a745'}} />
            <div>
              <h3>פעילות</h3>
              <p className={styles.statValue}>{activeCompanies}</p>
            </div>
          </div>
          <div className={styles.statCard}>
            <Shield size={24} className={styles.statIcon} style={{color: '#dc3545'}} />
            <div>
              <h3>לא פעילות</h3>
              <p className={styles.statValue}>{inactiveCompanies}</p>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.actions}>
        <div className={styles.filtersContainer}>
          <div className={styles.searchBar}>
            <input
              type="text"
              placeholder="חיפוש חברות ניהול (שם, איש קשר, טלפון, דוא&quot;ל, כתובת, פעיל/לא פעיל)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Search size={20} className={styles.searchIcon} />
          </div>

          <div className={styles.statusFilter}>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={styles.statusSelect}
            >
              <option value="all">כל הסטטוסים</option>
              <option value="active">פעילות</option>
              <option value="inactive">לא פעילות</option>
            </select>
            <Shield size={18} className={styles.filterIcon} />
          </div>

          {(searchTerm || statusFilter !== 'active') && (
            <button 
              className={styles.clearFiltersButton}
              onClick={clearFilters}
              title="נקה מסננים"
            >
              <Filter size={18} />
              נקה
            </button>
          )}
        </div>
        
        <div className={styles.actionButtons}>
          {/* Desktop buttons group */}
          <div className={styles.actionButtonGroup}>
            <button className={styles.addButton} onClick={handleAddCompany}>
              <Plus size={20} />
              הוסף חברת ניהול חדשה
            </button>
            
            {/* Export to Excel Button */}
            <div className={styles.exportButton}>
              <div
                className={styles.exportButtonContent}
                onClick={() => setIsExportDropdownOpen(!isExportDropdownOpen)}
                disabled={isExporting || filteredCompanies.length === 0}
              >
                {isExporting ? (
                  <div className={styles.exportIcon}>⏳</div>
                ) : (
                  <FileSpreadsheet className={styles.exportIcon} />
                )}
                ייצא לאקסל
                <ChevronDown className={styles.exportDropdownArrow} />
              </div>

              {isExportDropdownOpen && (
                <div className={styles.exportDropdownMenu}>
                  <button
                    onClick={() => {
                      exportToExcel(false);
                      setIsExportDropdownOpen(false);
                    }}
                    disabled={isExporting || currentCompanies.length === 0}
                  >
                    ייצא עמוד נוכחי ({currentCompanies.length} חברות)
                  </button>

                  <button
                    onClick={() => {
                      exportToExcel(true);
                      setIsExportDropdownOpen(false);
                    }}
                    disabled={isExporting || filteredCompanies.length === 0}
                  >
                    ייצא את כל הנתונים ({filteredCompanies.length} חברות)
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Mobile dropdown menu */}
          <div className={styles.mobileButtonsDropdown} ref={mobileDropdownRef}>
            <button
              onClick={() => setIsMobileDropdownOpen(!isMobileDropdownOpen)}
              className={styles.mobileDropdownToggle}
            >
              <Menu size={20} />
              פעולות
              <ChevronDown className={`${styles.dropdownArrow} ${isMobileDropdownOpen ? styles.dropdownArrowOpen : ''}`} />
            </button>

            {isMobileDropdownOpen && (
              <div className={styles.mobileDropdownMenu}>
                <button
                  onClick={() => {
                    handleAddCompany();
                    setIsMobileDropdownOpen(false);
                  }}
                  className={styles.mobileDropdownItem}
                >
                  <Plus size={18} />
                  הוסף חברת ניהול חדשה
                </button>

                <div className={styles.menuSeparator}></div>

                <button
                  onClick={() => {
                    exportToExcel(false);
                    setIsMobileDropdownOpen(false);
                  }}
                  disabled={isExporting || currentCompanies.length === 0}
                  className={styles.mobileDropdownItem}
                >
                  <Download size={18} />
                  ייצא עמוד נוכחי ({currentCompanies.length} חברות)
                </button>

                <button
                  onClick={() => {
                    exportToExcel(true);
                    setIsMobileDropdownOpen(false);
                  }}
                  disabled={isExporting || filteredCompanies.length === 0}
                  className={styles.mobileDropdownItem}
                >
                  <Download size={18} />
                  ייצא את כל הנתונים ({filteredCompanies.length} חברות)
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner}></div>
          <p>טוען חברות ניהול...</p>
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className={styles.tableContainer}>
            <table className={styles.companiesTable}>
              <thead>
                <tr>
                  <th>שם החברה</th>
                  <th>איש קשר</th>
                  <th>טלפון</th>
                  <th>דוא"ל</th>
                  <th>כתובת</th>
                  <th>פעיל</th>
                  <th>פעולות</th>
                </tr>
              </thead>
              <tbody>
                {currentCompanies.length > 0 ? (
                  currentCompanies.map((company, index) => {
                    
                    return (
                      <tr key={`${company.ManagementCompanyID || index}-${index}`}>
                        <td className={styles.companyName}>
                          {company.Name || 'No Name'}
                        </td>
                        <td>{company.ContactPerson || 'לא צוין'}</td>
                        <td>{company.Phone || 'לא צוין'}</td>
                        <td>{company.Email || 'לא צוין'}</td>
                        <td>{company.Address || 'לא צוין'}</td>
                        <td>
                          <button
                            className={`${styles.statusBadgeTable} ${company.IsActive ? styles.statusActive : styles.statusInactive}`}
                            onClick={() => handleToggleActiveStatus(company.ManagementCompanyID)}
                            title={company.IsActive ? 'לחץ להשבתה' : 'לחץ להפעלה'}
                          >
                            {company.IsActive ? 'פעיל' : 'לא פעיל'}
                          </button>
                        </td>
                        <td className={styles.actionsCell}>
                          <ActionMenu
                            company={company}
                            onEdit={handleEditCompany}
                            onDelete={handleDeleteCompany}
                            loading={loading}
                          />
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="7" className={styles.noResults}>
                      {searchTerm || statusFilter !== 'active' ? 'לא נמצאו חברות ניהול התואמות לחיפוש' : 'אין חברות ניהול פעילות'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards View */}
          <div className={styles.mobileCardsContainer}>
            <div className={styles.cardsGrid}>
              {currentCompanies.length > 0 ? (
                currentCompanies.map((company, index) => (
                  <ManagementCompanyCard
                    key={`${company.ManagementCompanyID || index}-${index}`}
                    company={company}
                    onEdit={handleEditCompany}
                    onDelete={handleDeleteCompany}
                    onToggleStatus={handleToggleActiveStatus}
                  />
                ))
              ) : (
                <div className={styles.emptyState}>
                  {searchTerm || statusFilter !== 'active' ? 'לא נמצאו חברות ניהול התואמות לחיפוש' : 'אין חברות ניהול פעילות'}
                </div>
              )}
            </div>
          </div>

          {filteredCompanies.length > 0 && (
            <div className={styles.pagination}>
              <div className={styles.paginationControls}>
                <button
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={styles.paginationButton}
                >
                  <ChevronRight size={20} />
                </button>
                <span className={styles.pageInfo}>
                  עמוד {currentPage} מתוך {totalPages}
                </span>
                <button
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={styles.paginationButton}
                >
                  <ChevronLeft size={20} />
                </button>
              </div>
              <div className={styles.resultsPerPage}>
                <span>תוצאות בעמוד:</span>
                <select
                  value={companiesPerPage}
                  onChange={(e) => {
                    setCompaniesPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={15}>15</option>
                  <option value={20}>20</option>
                </select>
              </div>
              <div className={styles.totalInfo}>
                מציג {indexOfFirstCompany + 1}-{Math.min(indexOfLastCompany, filteredCompanies.length)} מתוך {filteredCompanies.length} חברות ניהול
              </div>
            </div>
          )}
        </>
      )}
      
      <ErrorPopup
        isOpen={errorPopup.isOpen}
        onClose={() => setErrorPopup({ isOpen: false, title: '', message: '' })}
        title={errorPopup.title}
        message={errorPopup.message}
      />
    </div>
  );
};

export default ManagementCompany;